import React, { useEffect, useRef, useState } from "react";
import { Download, Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";

const W = 1200;
const H = 630;
const PAPER = "#FDFBF7";
const INK = "#2C2A28";
const INK_SOFT = "#5C5854";
const TERRACOTTA = "#C86A51";
const SAGE = "#839788";
const RULE = "#E6E2D8";

const CATEGORY_LABEL = {
  childrens_books: "Children's Book",
  poems: "Poem",
  articles: "Article",
};

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

async function ensureFontsReady() {
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.load('600 90px "Cormorant Garamond"');
      await document.fonts.load('400 24px "Work Sans"');
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
}

function drawCard(canvas, { title, category, authorName }) {
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;

  // Background
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, W, H);

  // Subtle paper grain dots
  ctx.fillStyle = "rgba(44,42,40,0.04)";
  for (let y = 0; y < H; y += 4) {
    for (let x = 0; x < W; x += 4) {
      if ((x + y) % 8 === 0) ctx.fillRect(x, y, 1, 1);
    }
  }

  // Inner frame border
  const pad = 64;
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 1;
  ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2);

  // Decorative big quote mark (top-left, inside frame)
  ctx.fillStyle = "rgba(200,106,81,0.18)";
  ctx.font = '600 220px "Cormorant Garamond", Georgia, serif';
  ctx.textBaseline = "top";
  ctx.fillText("“", pad + 24, pad + 8);

  // Brand row (top-right)
  ctx.fillStyle = INK_SOFT;
  ctx.font = '500 18px "Work Sans", system-ui, sans-serif';
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "right";
  const brand = "B. RUFF · WRITES";
  ctx.fillText(brand, W - pad - 24, pad + 44);

  // Compute title sizing & wrapping first
  const textLeft = pad + 48;
  const titleMaxW = W - pad * 2 - 96;
  let titleSize = 96;
  let lines;
  while (titleSize > 52) {
    ctx.font = `600 ${titleSize}px "Cormorant Garamond", Georgia, serif`;
    lines = wrapLines(ctx, title || "Untitled", titleMaxW);
    if (lines.length <= 3) break;
    titleSize -= 6;
  }
  const lineH = titleSize * 1.05;
  const titleBlockH = lines.length * lineH;

  // Vertically center the title block (with kicker above + author below)
  const kickerGap = 42;
  const ruleGap = 36;
  const authorGap = 60;
  const totalBlockH = 28 + kickerGap + titleBlockH + ruleGap + authorGap;
  const blockTop = (H - totalBlockH) / 2;

  // Category kicker
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = TERRACOTTA;
  ctx.font = '600 22px "Work Sans", system-ui, sans-serif';
  ctx.letterSpacing = "0.18em";
  const kicker = (CATEGORY_LABEL[category] || "Writing").toUpperCase();
  const kickerY = blockTop + 22;
  ctx.fillText(kicker, textLeft, kickerY);

  // Title
  ctx.fillStyle = INK;
  ctx.font = `600 ${titleSize}px "Cormorant Garamond", Georgia, serif`;
  ctx.textBaseline = "top";
  let y = kickerY + kickerGap;
  for (const line of lines.slice(0, 4)) {
    ctx.fillText(line, textLeft, y);
    y += lineH;
  }

  // Hairline rule
  const ruleY = y + 18;
  ctx.strokeStyle = TERRACOTTA;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(textLeft, ruleY);
  ctx.lineTo(textLeft + 80, ruleY);
  ctx.stroke();

  // Author line
  ctx.fillStyle = INK_SOFT;
  ctx.font = '500 22px "Work Sans", system-ui, sans-serif';
  ctx.textBaseline = "alphabetic";
  ctx.fillText(`— ${authorName || "Barbara Ruffing"}`, textLeft, ruleY + 38);

  // Footer pill
  ctx.fillStyle = SAGE;
  ctx.font = '500 16px "Work Sans", system-ui, sans-serif';
  ctx.textAlign = "right";
  ctx.fillText("bruffwrites.com", W - pad - 24, H - pad - 16);
}

export default function ShareCard({ title, category, authorName }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      await ensureFontsReady();
      if (cancelled || !canvasRef.current) return;
      drawCard(canvasRef.current, { title, category, authorName });
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, title, category, authorName]);

  const getBlob = () =>
    new Promise((resolve) =>
      canvasRef.current.toBlob((b) => resolve(b), "image/png"),
    );

  const handleCopy = async () => {
    try {
      const blob = await getBlob();
      if (!navigator.clipboard || !window.ClipboardItem) {
        throw new Error("Clipboard image not supported in this browser");
      }
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopied(true);
      toast.success("Share card copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error(
        "Copy not supported here — use Download instead.",
      );
    }
  };

  const handleDownload = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "writing")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "writing"}-share.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Share card downloaded");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="share-card-open-btn"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta link-underline"
      >
        <Share2 size={14} /> Create a share card
      </button>
    );
  }

  return (
    <div className="mt-6" data-testid="share-card-panel">
      <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
        Share card
      </p>
      <div className="border border-rule bg-paper-2/40 p-3">
        <canvas
          ref={canvasRef}
          data-testid="share-card-canvas"
          className="w-full h-auto block"
          style={{ aspectRatio: "1200 / 630" }}
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleCopy}
          disabled={!ready}
          data-testid="share-card-copy-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-60"
        >
          {copied ? (
            <>
              <Check size={14} /> Copied
            </>
          ) : (
            <>
              <Copy size={14} /> Copy to clipboard
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!ready}
          data-testid="share-card-download-btn"
          className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-60"
        >
          <Download size={14} /> Download PNG
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="share-card-close-btn"
          className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
        >
          Close
        </button>
      </div>
      <p className="mt-3 text-xs text-ink-soft italic">
        Tip: Paste directly into Instagram, Facebook, X, or a message — it's a
        1200×630 image, perfect for social previews.
      </p>
    </div>
  );
}
